import { Account } from "../../../api/auth/myProfilesWithData";
import { useProfilesSortedAndPartitionedByRecentConcerns } from "../../../api/metadata/useProfilesSortedAndPartitionedByRecentConcerns";
import { device } from "../../../constants/mediaQueries";
import { isAppTypeIframe } from "../../../getAppType";
import { useLocStore } from "../../../localization";
import { LocContext } from "../../../localization/LocContext";
import { useLogOut } from "../../../url/logOut";
import { useWhoAmI } from "../../../user/useWhoAmI";
import { useMediaQuery } from "../../../utils/style/useMediaQuery";
import { CustomMenuItem } from "../MenuItems/completeMenuItem";
import { DepartmentTemplate } from "./DepartmentTemplate";
import { useLanguageMenuItem } from "./useLanguageMenuItem";
import { useSettings } from "../../../settings/useSettings";
import { getRedirectProps } from "../../../url/redirect";
import { useVpnToolsMenuItem } from "./useVpnToolsMenuItem";
import { useKeycloakAuthMenuItem } from "./useKeycloakAuthMenuItem";
import {
  showErrorToastMsg,
  showToastMsg,
} from "../../../generic-components/messages/CvpToastMessages";
import { useLoginProps } from "../../../url/useLoginProps";
import { useKeycloakAuth } from "../../adminSettings/AdminContext";

function useDepartmentItems() {
  const L = useLocStore(LocContext.PROFILE);

  const { confirmedNoProfileInConcern, whoAmI, confirmedLoggedIn } =
    useWhoAmI();

  const partition = useProfilesSortedAndPartitionedByRecentConcerns({
    fetchUserMetadata: confirmedLoggedIn,
  });

  const profileUrl = (account: Account) => {
    return `?k=${account.concernId}`;
  };

  let departmentItems: CustomMenuItem[] = [];

  if (!confirmedLoggedIn) {
    return departmentItems;
  }

  if (confirmedNoProfileInConcern) {
    departmentItems.push({
      path: "/profile",
      label: whoAmI.details.concernName,
      icon: "pi pi-fw pi-plus",
    });
  }

  departmentItems.push({
    separator: true,
  });

  if (partition.isReady) {
    const { recentProfiles, otherProfiles } = partition;

    const concernMenuItem = (account: Account): CustomMenuItem => ({
      path: profileUrl(account),
      label: `${account.concernName}`,
      icon: "pi pi-fw pi-user",
      isSelected: () => account.concernId === window.cvpIdConcern,
      onClick: () => {
        window.location.href = profileUrl(account);
      },
    });

    const showRecentDivider =
      recentProfiles.length > 0 && otherProfiles.length > 0;

    departmentItems.push(
      ...recentProfiles.map(concernMenuItem),
      ...(showRecentDivider
        ? [
            {
              separator: true,
              className: "cvp-profile-concern-group-divider",
            } as CustomMenuItem,
          ]
        : []),
      ...otherProfiles.map(concernMenuItem)
    );
  } else {
    departmentItems.push({
      label: L.id("920ec96f8547").loc("Henter avdelinger..."),
      isSelected: () => false,
    });
  }

  // remove separators that are at the beginning or end of the list
  if (departmentItems[0]?.separator) {
    departmentItems.shift();
  }
  if (departmentItems[departmentItems.length - 1]?.separator) {
    departmentItems.pop();
  }

  if (!departmentItems.length) {
    departmentItems.push({
      label: L.id("ce0d8accb9c1").loc("Du har ingen profiler"),
      isSelected: () => false,
      disabled: true,
      onClick: () => {},
    });
  }

  return departmentItems;
}

export interface UseProfileDepartmentItemsProps {
  showDepartmentItems?: boolean;
  showMyProfile?: boolean;
  showLogOut?: boolean;
}
export function useProfileDepartmentItems(): CustomMenuItem[] {
  const L = useLocStore(LocContext.PROFILE);
  const departmentItems = useDepartmentItems();
  const {
    confirmedHasProfileInConcern,
    confirmedLoggedIn,
    confirmedNotLoggedIn,
    whoAmI,
  } = useWhoAmI();

  const logOut = useLogOut();
  const languageMenuItem = useLanguageMenuItem();
  const settings = useSettings();
  const vpnToolsMenuItem = useVpnToolsMenuItem();
  const keycloakAuthMenuItem = useKeycloakAuthMenuItem();
  const useKeycloak = useKeycloakAuth();

  const showDepartmentItems =
    confirmedHasProfileInConcern && confirmedLoggedIn && !isAppTypeIframe();

  const showMyProfile = confirmedLoggedIn && !!window.cvpIdConcern;
  const showLogOut = confirmedLoggedIn;
  const showLogIn = confirmedNotLoggedIn;

  let { redirectProps: loginRedirectProps } = useLoginProps();

  const items: CustomMenuItem[] = [];
  if (!settings?.iframe_overrideLocale) {
    items.push(languageMenuItem);
  }

  if (window.isLocalIp) {
    items.splice(0, 0, vpnToolsMenuItem, keycloakAuthMenuItem, {
      path: "/admin",
      target: "_blank",
      ...getRedirectProps("/admin", {
        mergeQs: {},
        target: "_blank",
      }),
      label: L.id("4e451af785fa").loc("Admin"),
      icon: "pi pi-fw pi-cog",
      className: "vpn-item",
    });
  }

  if (showMyProfile) {
    items.push({
      path: "/profile",
      label: confirmedHasProfileInConcern
        ? L.id("63d3d590076e").loc("Min profil")
        : L.id("fa0ae6774cda").loc("Opprett profil"),
      icon: confirmedHasProfileInConcern
        ? "pi pi-fw pi-user"
        : "pi pi-fw pi-plus",
    });
  }

  // Add Manage Account link for Keycloak users (change password, update MFA, etc.)
  if (confirmedLoggedIn && useKeycloak) {
    items.push({
      href: "https://auth.webtemp.no/realms/CVP/account",
      target: "_blank",
      label: L.id("eff82ed2cce0").loc("Administrer konto"),
      icon: "pi pi-fw pi-key",
      isSelected: () => false,
    });
  }

  if (showLogOut) {
    items.push({
      label: L.id("72bd38a1ab66").loc("Logg ut"),
      icon: "pi pi-fw pi-sign-out",
      className: "log-out",
      onClick: logOut,
    });
  }

  if (showLogIn) {
    items.push({
      ...loginRedirectProps,
      label: L.id("a918c8de02b7").loc("Logg inn"),
      icon: "pi pi-fw pi-sign-in",
    });
  }

  if (showDepartmentItems) {
    items.push(
      {
        separator: true,
      },
      {
        path: "",
        icon: "pi pi-fw pi-building",
        items: departmentItems,
        isSelected: () => false,
        template: DepartmentTemplate,
        className: "department",
      }
    );
  }

  if (confirmedLoggedIn) {
    items.push({
      path: "",
      icon: "pi pi-fw pi-copy",
      label: L.id("3acf453f1aec").split("Bruker-ID: {0}", [whoAmI?.id]),
      className: "guid",
      onClick: async () => {
        try {
          await navigator.clipboard.writeText(whoAmI?.id);
          showToastMsg(
            L.id("565b477e2a72").loc("Bruker-ID kopiert til utklippstavlen")
          );
        } catch (err) {
          showErrorToastMsg(err);
          console.log("Something went wrong", err);
        }
      },
    });
  }

  return items;
}
